"use client"

import { useEffect, useState, type CSSProperties } from "react"
import { getImageProps } from "next/image"
import type { HomeLayoutPopup } from "@src/types/home-layout"
import styles from "./popup-banner.module.css"

type Props = {
  popup?: HomeLayoutPopup | null
}

const DISMISS_DURATION_MS = 12 * 60 * 60 * 1000

const PopupBanner = ({ popup }: Props) => {
  const [visible, setVisible] = useState(false)

  const item = popup?.items?.[0]
  const desktop = item?.media?.desktop
  const mobile = item?.media?.mobile
  const fallback = mobile || desktop
  const dismissalKey =
    popup && fallback?.url
      ? `home-popup-dismissed:${popup.id ?? fallback.url}`
      : null

  useEffect(() => {
    if (!popup || !fallback?.url || !dismissalKey) return

    const dismissedAt = Number(localStorage.getItem(dismissalKey))
    const isDismissed =
      Number.isFinite(dismissedAt) &&
      Date.now() - dismissedAt < DISMISS_DURATION_MS

    setVisible(!isDismissed)
  }, [popup, fallback?.url, dismissalKey])

  if (!visible || !popup || !fallback?.url) return null

  const desktopMedia = desktop || fallback
  const mobileMedia = mobile || desktopMedia
  const dimensions = (media: typeof desktopMedia) => {
    return media.width && media.width > 0 && media.height && media.height > 0
      ? { width: media.width, height: media.height }
      : { width: 1000, height: 1000 }
  }
  const desktopSize = dimensions(desktopMedia)
  const mobileSize = dimensions(mobileMedia)
  const sizes =
    "(max-width: 767px) min(340px, calc(100vw - 32px)), min(600px, calc(100vw - 32px))"
  const desktopProps = getImageProps({
    src: desktopMedia.url,
    alt: item.title || "Popup banner",
    ...desktopSize,
    sizes
  }).props
  const mobileProps = getImageProps({
    src: mobileMedia.url,
    alt: item.title || "Popup banner",
    ...mobileSize,
    sizes
  }).props
  const modalStyle = {
    "--popup-ratio": `${desktopSize.width} / ${desktopSize.height}`,
    "--popup-ratio-mobile": `${mobileSize.width} / ${mobileSize.height}`,
    "--popup-width": `min(600px, calc(100vw - 32px), calc((100dvh - 32px) * ${desktopSize.width / desktopSize.height}))`,
    "--popup-width-mobile": `min(340px, calc(100vw - 32px), calc(72dvh * ${mobileSize.width / mobileSize.height}))`
  } as CSSProperties

  const closePopup = () => {
    setVisible(false)
    if (dismissalKey) {
      localStorage.setItem(dismissalKey, String(Date.now()))
    }
  }

  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-label={item.title || "Popup banner"}
      onClick={closePopup}
    >
      <div
        className={styles.modal}
        style={modalStyle}
        onClick={event => event.stopPropagation()}
      >
        <button
          type="button"
          className={styles.close}
          aria-label="Close popup"
          onClick={closePopup}
        >
          ×
        </button>

        <picture className={styles.image}>
          <source
            media="(max-width: 767px)"
            srcSet={mobileProps.srcSet}
            sizes={sizes}
            width={mobileSize.width}
            height={mobileSize.height}
          />
          <img {...desktopProps} loading="eager" fetchPriority="auto" />
        </picture>
      </div>
    </div>
  )
}

export default PopupBanner
